import React from "react";
import { Feed } from "subframe/components/Feed";
import { formatLocalDate } from "utils/helper";
import { TaskCenterStatusMap } from "modules/Retention/types/taskCenter";
import { FeedItem, BadgeVariant } from "modules/Retention/types";
import TimelineEntry from "./TimelineEntry";

interface ExtendedFeedItem extends Omit<FeedItem, 'type' | 'iconVariant'> {
  type: 'system' | 'action' | 'survey' | 'user_note' | 'system_message';
  iconVariant?: BadgeVariant | string;
  noter_name?: string;
  note_tags?: string[];
  event_type?: string;
  event_subtype?: string;
  metadata?: Record<string, any>;
  actor?: string;
  status?: string;
  sequence?: number;
}

interface FeedTimelineProps {
  items: ExtendedFeedItem[];
  isLoading?: boolean;
}

const FeedTimelineSkeleton: React.FC = () => (
  <div className="flex w-full flex-col items-start gap-4 px-2 md:px-6">
    <Feed>
      {[1, 2, 3].map((item) => (
        <div key={item} className="flex w-full items-start gap-3 md:gap-4">
          <div className="flex flex-col items-center self-stretch">
            <div className="flex h-4 w-0.5 flex-none flex-col items-start gap-2 bg-neutral-border" />
            <div className="flex flex-col items-start gap-1">
              <div className="h-8 w-8 rounded-full bg-neutral-200 animate-pulse" />
            </div>
            {item < 3 && (
              <div className="flex w-0.5 grow shrink-0 basis-0 flex-col items-start gap-2 bg-neutral-border" />
            )}
          </div>
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 pt-1 pb-7">
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <div className="flex grow shrink-0 basis-0 flex-wrap items-start gap-1">
                <div className="h-4 w-32 bg-neutral-200 rounded animate-pulse" />
                <div className="h-4 w-48 bg-neutral-200 rounded animate-pulse" />
              </div>
              <div className="h-4 w-24 bg-neutral-200 rounded animate-pulse" />
            </div>
          </div>
        </div>
      ))}
    </Feed>
  </div>
);

const getStatusVariant = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'AT_RISK':
      return 'pending';
    case 'IN_PROGRESS':
      return 'pending';
    case 'RESOLVED':
      return 'resolved';
    case 'DISMISSED':
      return 'dismissed';
    default:
      return 'pending';
  }
};

const getStatusDisplayText = (status: string) => {
  // Convert API status to the format expected by TaskCenterStatusMap
  const statusKey = status?.toLowerCase().replace('at_risk', 'at_risk').replace('in_progress', 'in_progress');
  return TaskCenterStatusMap[statusKey as keyof typeof TaskCenterStatusMap] || status;
};

const getIconVariant = (eventType: string, status: string) => {
  switch (eventType) {
    case 'ESCALATION_CREATED':
      return 'error';
    case 'SURVEY_COMPLETED':
      return 'neutral';
    case 'SURVEY_CREATED':
      return 'neutral';
    case 'FOLLOWUP_ADDED':
      return 'neutral';
    default:
      if (status === 'RESOLVED') return 'success';
      return 'neutral';
  }
};

const getIcon = (eventType: string, status: string) => {
  switch (eventType) {
    case 'ESCALATION_CREATED':
      return 'FeatherAlertCircle';
    case 'SURVEY_COMPLETED':
      return 'FeatherCheckSquare';
    case 'SURVEY_CREATED':
      return 'FeatherSend';
    case 'FOLLOWUP_ADDED':
      return 'FeatherUser';
    default:
      if (status === 'RESOLVED') return 'FeatherCheck';
      return 'FeatherClock';
  }
};

export const FeedTimeline: React.FC<FeedTimelineProps> = ({ items, isLoading = false }) => {
  if (isLoading) {
    return <FeedTimelineSkeleton />;
  }

  if (!items || items.length === 0) {
    return (
      <div className="flex w-full flex-col items-start gap-4 px-2 md:px-6">
        <div className="flex w-full items-center justify-center py-8">
          <span className="text-body font-body text-subtext-color">No follow-up actions found</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start px-2 md:px-6">
      <div className="flex w-full flex-col items-start">
        {items.map((item, idx) => {
          const isLastItem = idx === items.length - 1;
          
          return (
            <TimelineEntry
              key={item.id}
              item={item}
              isLastItem={isLastItem}
              getIconVariant={getIconVariant}
              getIcon={getIcon}
              getStatusVariant={getStatusVariant}
              getStatusDisplayText={getStatusDisplayText}
            />
          );
        })}
      </div>
    </div>
  );
}; 